import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChangePinModal } from './ChangePinModal'
import { CashierPinMismatchError, useChangeCashierPin } from './useChangeCashierPin'
import {
  InvalidCashierPinError,
  useVerifyCurrentCashierPin,
} from './useVerifyCurrentCashierPin'

type ChangePinStep = 'current' | 'new' | 'confirm' | 'success'
type PinEntryStep = Exclude<ChangePinStep, 'success'>

interface ChangeCashierPinFlowProps {
  visible: boolean
  onClose: () => void
}

interface StepCopy {
  title: string
  subtitle: string
  modalAccessibilityLabel: string
}

export function ChangeCashierPinFlow({ visible, onClose }: ChangeCashierPinFlowProps) {
  const { t } = useTranslation()
  const verifyCurrentPinMutation = useVerifyCurrentCashierPin()
  const changeCashierPinMutation = useChangeCashierPin()
  const [step, setStep] = useState<ChangePinStep>('current')
  const [cashierId, setCashierId] = useState('')
  const [currentPin, setCurrentPin] = useState('')
  const [newPin, setNewPin] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const resetAndClose = () => {
    setStep('current')
    setCashierId('')
    setCurrentPin('')
    setNewPin('')
    setErrorMessage(null)
    onClose()
  }

  const moveToStep = (nextStep: ChangePinStep) => {
    setStep(nextStep)
    setErrorMessage(null)
  }

  const handleConfirm = (pin: string) => {
    if (step === 'current') {
      verifyCurrentPinMutation.mutate(pin, {
        onSuccess: (verifiedCashierId) => {
          setCashierId(verifiedCashierId)
          setCurrentPin(pin)
          moveToStep('new')
        },
        onError: (error) => {
          setErrorMessage(
            error instanceof InvalidCashierPinError
              ? t('cashierChangePin.currentPinInvalidError')
              : t('cashierChangePin.updatePinNetworkError'),
          )
        },
      })
      return
    }

    if (step === 'new') {
      setNewPin(pin)
      moveToStep('confirm')
      return
    }

    if (pin !== newPin) {
      setErrorMessage(t('cashierChangePin.confirmPinMismatchError'))
      return
    }

    if (!cashierId || !currentPin) {
      setErrorMessage(t('cashierChangePin.updatePinNetworkError'))
      return
    }

    changeCashierPinMutation.mutate(
      { cashierId, currentPin, newPin, confirmPin: pin },
      {
        onSuccess: () => {
          setCashierId('')
          setCurrentPin('')
          setNewPin('')
          moveToStep('success')
        },
        onError: (error) => {
          if (error instanceof CashierPinMismatchError) {
            setErrorMessage(t('cashierChangePin.confirmPinMismatchError'))
            return
          }

          setErrorMessage(
            error instanceof InvalidCashierPinError
              ? t('cashierChangePin.currentPinInvalidError')
              : t('cashierChangePin.updatePinNetworkError'),
          )
        },
      },
    )
  }

  if (step === 'success') {
    return (
      <ChangePinModal
        mode="success"
        visible={visible}
        title={t('cashierChangePin.successTitle')}
        subtitle={t('cashierChangePin.successSubtitle')}
        doneLabel={t('cashierChangePin.doneButton')}
        modalAccessibilityLabel={t('cashierChangePin.successModalAccessibilityLabel')}
        imageAccessibilityLabel={t('cashierChangePin.successImageAccessibilityLabel')}
        closeAccessibilityLabel={t('cashierChangePin.closeModalAccessibilityLabel')}
        onClose={resetAndClose}
      />
    )
  }

  const stepCopy = getStepCopy(step, t)
  return (
    <ChangePinModal
      mode="entry"
      visible={visible}
      title={stepCopy.title}
      subtitle={stepCopy.subtitle}
      modalAccessibilityLabel={stepCopy.modalAccessibilityLabel}
      closeAccessibilityLabel={t('cashierChangePin.closeModalAccessibilityLabel')}
      errorMessage={errorMessage}
      isLoading={
        (step === 'current' && verifyCurrentPinMutation.isPending) ||
        (step === 'confirm' && changeCashierPinMutation.isPending)
      }
      getCodeAccessibilityLabel={(entered, total) =>
        t('cashierChangePin.pinProgressAccessibilityLabel', { entered, total })}
      onInputChange={() => setErrorMessage(null)}
      onClose={resetAndClose}
      onConfirm={handleConfirm}
    />
  )
}

function getStepCopy(step: PinEntryStep, t: ReturnType<typeof useTranslation>['t']): StepCopy {
  switch (step) {
    case 'current':
      return {
        title: t('cashierChangePin.currentPinTitle'),
        subtitle: t('cashierChangePin.currentPinSubtitle'),
        modalAccessibilityLabel: t('cashierChangePin.currentPinModalAccessibilityLabel'),
      }
    case 'new':
      return {
        title: t('cashierChangePin.newPinTitle'),
        subtitle: t('cashierChangePin.newPinSubtitle'),
        modalAccessibilityLabel: t('cashierChangePin.newPinModalAccessibilityLabel'),
      }
    case 'confirm':
      return {
        title: t('cashierChangePin.confirmPinTitle'),
        subtitle: t('cashierChangePin.confirmPinSubtitle'),
        modalAccessibilityLabel: t('cashierChangePin.confirmPinModalAccessibilityLabel'),
      }
  }
}
